/**
 * SEO / meta tag injection helper for public pages.
 *
 * arcee-client is a CRA (client-side rendered) app. Google's crawler handles
 * JS-rendered pages fine. For social preview bots (LinkedIn, Slack, Twitter)
 * consider adding a thin SSR prerender service in Phase 2.
 */

import { richTextToPlain } from './sanitizeRichText';

/**
 * Sets document title and all relevant meta tags for a public page.
 *
 * @param {Object} seo - SEO config from the API
 * @param {string} [seo.meta_title]
 * @param {string} [seo.meta_description]
 * @param {string} [seo.og_title]
 * @param {string} [seo.og_description]
 * @param {string} [seo.og_image_url]
 * @param {string} [seo.hashtags] - comma-separated tags used across all platforms, e.g. "travel,food"
 * @param {Object} [appBranding] - application-level branding, used to fall back the OG/Twitter
 *   image when the page has no `og_image_url` configured.
 * @param {string} [appBranding.banner_image]
 * @param {string} [appBranding.logo]
 */
export function applyPageSEO({ meta_title, meta_description, og_title, og_description, og_image_url, hashtags } = {}, appBranding = {}) {
  if (meta_title) document.title = meta_title;

  // Standard meta
  setMeta('description', meta_description || '');

  // Falls back to the application's banner image, then its logo, when the
  // page/table/record has no OG image of its own configured.
  const image = og_image_url || appBranding?.banner_image || appBranding?.logo || '';

  // Open Graph (Facebook, LinkedIn, etc.)
  setMeta('og:title', og_title || meta_title || '');
  setMeta('og:description', og_description || meta_description || '');
  setMeta('og:type', 'website');
  setMeta('og:url', window.location.href);
  if (image) {
    setMeta('og:image', image);
  }

  // Twitter Card — override the static defaults in index.html
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', og_title || meta_title || '');
  setMeta('twitter:description', og_description || meta_description || '');
  setMeta('twitter:url', window.location.href);
  if (image) {
    setMeta('twitter:image', image);
  }

  // Facebook article:tag — one <meta property="article:tag"> per tag.
  // Remove any previously injected tags before re-applying so stale tags
  // from a prior navigation don't accumulate.
  clearArticleTags();
  const fbTags = parseTags(hashtags);
  fbTags.forEach(tag => {
    const el = document.createElement('meta');
    el.setAttribute('property', 'article:tag');
    el.setAttribute('content', tag);
    el.setAttribute('data-arcee-tag', 'true'); // marker for clearArticleTags()
    document.head.appendChild(el);
  });

  // Canonical link
  setCanonical(window.location.href);
}

const RECORD_SOURCEABLE_SEO_KEYS = [
  'meta_title',
  'meta_description',
  'og_title',
  'og_description',
  'og_image_url',
];

/** The two keys the server truncates, and the length it truncates them to. */
const DESCRIPTION_KEYS = new Set(['meta_description', 'og_description']);
const MAX_DESCRIPTION_CHARS = 200;

/**
 * A record value as one line of plain text, for a meta tag.
 *
 * Mirror of `_plain_text` in arcee's prerender_views.py — same stripping, same
 * whitespace collapse, same 200-character ellipsis — so a crawler reading the
 * prerendered page and a browser reading the live one get the same card.
 */
function seoPlainText(value, limit) {
  let text = richTextToPlain(String(value));
  text = text.split(/\s+/).filter(Boolean).join(' ');
  if (limit && text.length > limit) {
    text = `${text.slice(0, limit - 1).trimEnd()}…`;
  }
  return text;
}

/**
 * Resolves a table's SEO config against one record, substituting any
 * property whose admin-configured `<key>_field` names a field that has
 * a non-empty value on this record. Properties without a configured
 * source (or whose sourced field is blank) keep their manual value.
 * `hashtags` is never field-sourced.
 *
 * @param {Object} [seo] - SEO config from the API (may include `<key>_field` sources)
 * @param {Object} [record] - the record being rendered
 * @returns {Object} resolved SEO config with the same shape as `seo`
 */
export function resolveSeoForRecord(seo = {}, record = {}) {
  const resolved = { ...seo };
  RECORD_SOURCEABLE_SEO_KEYS.forEach(key => {
    const fieldName = seo[`${key}_field`];
    if (fieldName) {
      const value = record[fieldName];
      if (value !== undefined && value !== null && value !== '') {
        // resolved[key] = value;
        // A sourced column may be rich text — an event description is. Tags and
        // entities would otherwise appear verbatim in the share card. The
        // server-side twin (prerender_views._plain_text) has always done this;
        // this side had not, so the same event could produce a clean card to a
        // crawler and a card full of <p> to anything reading the live page.
        resolved[key] = seoPlainText(value, DESCRIPTION_KEYS.has(key) ? MAX_DESCRIPTION_CHARS : null);
      }
    }
  });
  return resolved;
}

/**
 * Parses a comma-separated hashtag string into a clean array.
 * Strips leading # signs and filters empty entries.
 * e.g. "#travel, food, #art" → ["travel", "food", "art"]
 *
 * @param {string} [str]
 * @returns {string[]}
 */
export function parseTags(str) {
  if (!str || typeof str !== 'string') return [];
  return str
    .split(',')
    .map(t => t.trim().replace(/^#/, ''))
    .filter(Boolean);
}

function clearArticleTags() {
  document.querySelectorAll('meta[data-arcee-tag="true"]').forEach(el => el.remove());
}

function setMeta(name, content) {
  // Open Graph (og:*, article:*) uses property="..."; Twitter Cards use
  // name="...", which is also how index.html declares them. Getting this wrong
  // means querySelector misses the existing tag and appends a second one under
  // the other attribute, leaving the stale build-time value live beside it.
  const usesProperty = name.startsWith('og:') || name.startsWith('article:');
  const attr = usesProperty ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Overrides the static favicon links in index.html with the app's own logo,
 * once it's fetched from the backend. No-op if no logo is configured — the
 * static Arcee default set in index.html stays as the fallback.
 *
 * @param {string} [logoUrl]
 */
export function applyFavicon(logoUrl) {
  if (!logoUrl) return;
  ['icon', 'alternate icon', 'apple-touch-icon'].forEach(rel => {
    document.querySelectorAll(`link[rel="${rel}"]`).forEach(link => {
      link.setAttribute('href', logoUrl);
      link.removeAttribute('type');
    });
  });
}
